import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Leaf, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const LEAF_TEX = "https://images.unsplash.com/photo-1540927550647-43699cb14916?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxwbGFudCUyMGxlYWYlMjB0ZXh0dXJlJTIwbWFjcm98ZW58MHx8fHwxNzc5NjE5MjUxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name || res.user.email}`);
      navigate(loc.state?.from || "/scan");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sand-50">
      <div className="hidden lg:block relative">
        <img src={LEAF_TEX} alt="Leaf" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-forest/85 via-forest/55 to-ochre/40" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <Leaf size={22} weight="duotone" />
            <span className="font-display text-3xl">A Must For Earth</span>
          </Link>
          <div>
            <h2 className="font-display text-5xl leading-tight max-w-md">
              The diagnosis<br /><em className="italic text-ochre">your climate</em><br />deserves.
            </h2>
            <p className="font-mono-data text-white/80 mt-6">phyto · climate · 2026</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md" data-testid="login-page">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <Leaf size={22} weight="duotone" className="text-forest" />
            <span className="font-display text-2xl text-ink">A Must For Earth</span>
          </Link>
          <div className="font-mono-data text-mute mb-2">welcome back</div>
          <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Sign in.</h1>
          <p className="text-mute mt-2">Resume your scan history and continue diagnosing.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="font-mono-data text-mute">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                placeholder="you@plants.earth"
              />
            </div>
            <div>
              <label className="font-mono-data text-mute">Password</label>
              <div className="relative mt-1">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="login-password-input"
                  className="w-full bg-white border border-sand-200 rounded-xl px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-forest"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-mute"
                  aria-label="Toggle password"
                  data-testid="login-show-password"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && <div className="text-sm text-terra" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={busy} data-testid="login-submit" className="btn-primary w-full justify-center">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>

          <p className="mt-6 text-sm text-mute">
            New here? <Link to="/register" className="text-forest underline" data-testid="login-to-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
